import { useCallback } from 'react';
import { enqueueSnackbar } from 'notistack';
import { translate as t } from 'hooks/useTranslations';
import { Template, TemplateField, SectionFieldOption } from '../../_types';
import { Field, FieldOption } from 'views/App/Portal/PerformanceReporting/FieldBank/_types';
import { validateFieldOptions } from 'views/App/Portal/PerformanceReporting/_utils/fieldOptionValidation';
import { isSectionLockedByParent } from '../../_utils/sectionComparison';
import { getFieldUniqueId } from '../context';

export const useFieldManagement = (
  templateEditable: Template,
  setTemplateEditable: (template: Template) => void,
  trackActivity: () => void,
  triggerAutoSave: (template: Template, note: string) => void,
  selectedFieldForEditing: TemplateField | null,
  setSelectedFieldForEditingState: (field: TemplateField | null) => void,
  openFieldOptionsId: string | null,
  setOpenFieldOptionsIdState: (id: string | null) => void,
  setFieldOptionValidationErrors: (
    errors: Record<string, string> | ((prev: Record<string, string>) => Record<string, string>)
  ) => void,
  removableChildFieldIds: Set<number>,
  duplicateFieldIds: Set<number>,
  inactiveFieldIds: Set<number>
) => {
  // Determine if this is a child template (has a parent) for inheritance logic
  const isChildTemplate = !!templateEditable.parentTemplateId;

  // Template operations
  const addFieldToSection = useCallback(
    async (field: Field, sectionId: number, sectionGuid?: string) => {
      // Track user activity
      trackActivity();

      // Check if the field already exists in ANY section of the template
      const fieldAlreadyExists = templateEditable.templateSections.some((section) =>
        section.sectionFields.some(
          (existingField) =>
            existingField.fieldId === field.id && !existingField.isDeleted && !existingField.effectiveIsDeleted
        )
      );

      if (fieldAlreadyExists) {
        enqueueSnackbar(t('performanceReporting.templates.builder.messages.fieldAlreadyUsed'), {
          variant: 'warning',
        });
        return;
      }

      // Find target section using guid if id is 0
      const targetSection =
        sectionId === 0 && sectionGuid
          ? templateEditable.templateSections.find((s) => s.sectionGuid === sectionGuid)
          : templateEditable.templateSections.find((s) => s.id === sectionId);

      if (!targetSection) {
        console.error('Target section not found', { sectionId, sectionGuid });
        return;
      }

      // Create the new field
      // New fields always have parentSectionFieldId: null, so they are not inherited
      // Therefore, label should always have a value (not null) for new fields
      const newField: TemplateField = {
        id: 0, // Backend will assign new ID when saved
        templateSectionId: sectionId, // Associate field with section
        fieldId: field.id,
        name: field.name,
        label: field.label ?? field.name, // New fields always need a label value (use name as fallback)
        effectiveLabel: field.label ?? field.name, // Use label or fallback to name for UI display
        description: field.description,
        defaultValues: Array.isArray(field.defaultValues) ? field.defaultValues : field.defaultValues != null && field.defaultValues !== '' ? [String(field.defaultValues)] : [],
        effectiveDefaultValues: Array.isArray(field.defaultValues) ? field.defaultValues : field.defaultValues != null && field.defaultValues !== '' ? [String(field.defaultValues)] : [],
        fieldType: field.fieldType,
        fieldStatus: field.status,
        fieldCategoryId: field.fieldCategoryId,
        fieldCategory: field.fieldCategory,
        sectionFieldOptions: field.fieldOptions
          ? field.fieldOptions.map((option) => ({
              // DO NOT use spread operator - it includes isVisible from field catalog
              id: 0, // 0 = new option, backend will assign actual ID when saved
              fieldOptionId: option.id, // Reference to original field option
              sectionFieldId: undefined, // Will be set by backend
              label: option.label, // New fields have no parent; use catalog value
              rank: option.rank ?? 0,
              status: option.status ?? 'Active',
              isDeleted: false, // New options are not deleted
              effectiveIsDeleted: false,
              effectiveLabel: option.label ?? '', // Set effective label for UI display
              parentSectionFieldOptionId: null,
            }))
          : [],
        order: (templateEditable.templateSections.find((s) => s.id === sectionId)?.sectionFields.length || 0) + 1,
        rank: (templateEditable.templateSections.find((s) => s.id === sectionId)?.sectionFields.length || 0) + 1,
        isRequired: false, // New fields have no parent; use explicit value
        effectiveIsRequired: false, // Default display value
        isLocked: false, // New fields have no parent; use explicit value
        effectiveIsLocked: false, // Default display value
        isDeleted: false,
        effectiveIsDeleted: false,
        parentSectionFieldId: null,
        parentSectionField: null,
        // Backend will assign timestamps when saving new fields (id: 0)
      };

      // Create updated template - use guid for comparison when id is 0
      const updatedTemplate: Template = {
        ...templateEditable,
        templateSections: (templateEditable.templateSections || []).map((section) => {
          const isTargetSection =
            sectionId === 0 && sectionGuid ? section.sectionGuid === sectionGuid : section.id === sectionId;

          if (isTargetSection) {
            return {
              ...section,
              sectionFields: [...section.sectionFields, newField],
            };
          }
          return section;
        }),
      };

      // Update UI immediately (optimistic)
      setTemplateEditable(updatedTemplate);

      // Trigger auto-save in background
      triggerAutoSave(updatedTemplate, 'Added field to section');
    },
    [templateEditable, trackActivity, triggerAutoSave, isChildTemplate]
  );

  const removeFieldFromSection = useCallback(
    (sectionId: number, fieldId: number) => {
      // Find the section first
      const targetSection = templateEditable.templateSections.find((section) => section.id === sectionId);

      if (!targetSection) {
        console.error('Section not found for field removal');
        return;
      }

      // Check if section is locked by parent template
      // Allow removal if the field is a removable child field (child-specific field in a parent-locked section)
      // Also allow removal if the field has validation issues (duplicates/inactive must be removed before publish)
      const isRemovableChildField = removableChildFieldIds.has(fieldId);
      const isDuplicateField = duplicateFieldIds.has(fieldId);
      const isInactiveField = inactiveFieldIds.has(fieldId);
      if (isSectionLockedByParent(targetSection, templateEditable.parentTemplate) && !isRemovableChildField && !isDuplicateField && !isInactiveField) {
        enqueueSnackbar(t('performanceReporting.templates.builder.lockedSection.cannotRemoveField'), {
          variant: 'error',
        });
        return;
      }

      // Track user activity
      trackActivity();

      // Find the field to verify it exists
      const fieldToRemove = targetSection?.sectionFields.find((field) => field.id === fieldId);

      if (!fieldToRemove) {
        console.error('Field not found for removal');
        return;
      }

      // Close field properties if the removed field is currently selected
      // Use hybrid matching: compare by id if saved (id !== 0), otherwise by fieldId
      const isSelectedField = selectedFieldForEditing
        ? selectedFieldForEditing.id === 0
          ? selectedFieldForEditing.fieldId === fieldToRemove.fieldId
          : selectedFieldForEditing.id === fieldId
        : false;
      if (isSelectedField) {
        setSelectedFieldForEditingState(null);
      }

      // Close field options panel if the removed field has it open
      // openFieldOptionsId now tracks unique field identifier
      const fieldUniqueId = getFieldUniqueId(fieldToRemove);
      if (openFieldOptionsId === fieldUniqueId) {
        setOpenFieldOptionsIdState(null);
      }

      // Create the updated template with field soft deleted
      const updatedTemplate: Template = {
        ...templateEditable,
        templateSections: templateEditable.templateSections.map((section) => {
          if (section.id === sectionId) {
            return {
              ...section,
              sectionFields: section.sectionFields.map((f) =>
                f.id === fieldId ? { ...f, isDeleted: true, effectiveIsDeleted: true } : f
              ),
            };
          }
          return section;
        }),
      };

      // Update UI immediately (optimistic)
      setTemplateEditable(updatedTemplate);

      // Trigger auto-save in background
      triggerAutoSave(updatedTemplate, 'Removed field from section');
    },
    [
      templateEditable,
      selectedFieldForEditing,
      openFieldOptionsId,
      setSelectedFieldForEditingState,
      setOpenFieldOptionsIdState,
      trackActivity,
      triggerAutoSave,
    ]
  );

  // New function to add field to section at specific index
  const addFieldToSectionAtIndex = useCallback(
    async (field: Field, sectionId: number, insertIndex: number, sectionGuid?: string) => {
      // Track user activity
      trackActivity();

      // Check if the field already exists in ANY section of the template
      const fieldAlreadyExists = templateEditable.templateSections.some((section) =>
        section.sectionFields.some(
          (existingField) =>
            existingField.fieldId === field.id && !existingField.isDeleted && !existingField.effectiveIsDeleted
        )
      );

      if (fieldAlreadyExists) {
        enqueueSnackbar(t('performanceReporting.templates.builder.messages.fieldAlreadyAdded'), {
          variant: 'warning',
        });
        return;
      }

      // Find target section using guid if id is 0
      const targetSection =
        sectionId === 0 && sectionGuid
          ? templateEditable.templateSections.find((s) => s.sectionGuid === sectionGuid)
          : templateEditable.templateSections.find((s) => s.id === sectionId);

      if (!targetSection) {
        console.error('Target section not found', { sectionId, sectionGuid, insertIndex });
        return;
      }

      let updatedTemplate: Template;

      // Create the new field
      // New fields always have parentSectionFieldId: null, so they are not inherited
      // Therefore, label should always have a value (not null) for new fields
      const newField: TemplateField = {
        id: 0, // Backend will assign new ID when saved
        templateSectionId: sectionId, // Associate field with section
        fieldId: field.id,
        name: field.name,
        label: field.label ?? field.name, // New fields always need a label value (use name as fallback)
        effectiveLabel: field.label ?? field.name, // Use label or fallback to name for UI display
        description: field.description,
        defaultValues: Array.isArray(field.defaultValues) ? field.defaultValues : field.defaultValues != null && field.defaultValues !== '' ? [String(field.defaultValues)] : [],
        effectiveDefaultValues: Array.isArray(field.defaultValues) ? field.defaultValues : field.defaultValues != null && field.defaultValues !== '' ? [String(field.defaultValues)] : [],
        fieldType: field.fieldType,
        fieldStatus: field.status,
        fieldCategoryId: field.fieldCategoryId,
        fieldCategory: field.fieldCategory,
        sectionFieldOptions: field.fieldOptions
          ? field.fieldOptions.map((option) => ({
              // DO NOT use spread operator - it includes isVisible from field catalog
              id: 0, // 0 = new option, backend will assign actual ID when saved
              fieldOptionId: option.id, // Reference to original field option
              sectionFieldId: undefined, // Will be set by backend
              label: option.label, // New fields have no parent; use catalog value
              rank: option.rank ?? 0,
              status: option.status ?? 'Active',
              isDeleted: false, // New options are not deleted
              effectiveIsDeleted: false,
              effectiveLabel: option.label ?? '', // Set effective label for UI display
              parentSectionFieldOptionId: null,
            }))
          : [],
        order: insertIndex + 1, // Will be updated when we reorder
        rank: insertIndex + 1,
        isRequired: false, // New fields have no parent; use explicit value
        effectiveIsRequired: false, // Default display value
        isLocked: false, // New fields have no parent; use explicit value
        effectiveIsLocked: false, // Default display value
        isDeleted: false,
        effectiveIsDeleted: false,
        parentSectionFieldId: null,
        parentSectionField: null,
        // Backend will assign timestamps when saving new fields (id: 0)
      };

      // Create updated template - use guid for comparison when id is 0
      updatedTemplate = {
        ...templateEditable,
        templateSections: templateEditable.templateSections.map((section) => {
          const isTargetSection =
            sectionId === 0 && sectionGuid ? section.sectionGuid === sectionGuid : section.id === sectionId;

          if (isTargetSection) {
            const newFields = [...section.sectionFields];
            newFields.splice(insertIndex, 0, newField);

            // Update order numbers for all fields
            return {
              ...section,
              sectionFields: newFields.map((field, index) => ({
                ...field,
                order: index + 1,
              })),
            };
          }
          return section;
        }),
      };

      // Update UI immediately (optimistic)
      setTemplateEditable(updatedTemplate);

      // Trigger auto-save in background
      triggerAutoSave(updatedTemplate, 'Added field to section at specific position');
    },
    [templateEditable, trackActivity, triggerAutoSave, isChildTemplate]
  );

  // Field editing operations
  // fieldIdentifier can be either the template field id (for saved fields) or fieldId (for unsaved fields with id:0)
  const updateFieldLabel = useCallback(
    (fieldIdentifier: number, label: string) => {
      const updatedTemplate = {
        ...templateEditable,
        templateSections: templateEditable.templateSections.map((section) => ({
          ...section,
          sectionFields: section.sectionFields.map((field) => {
            // Match by id if saved (id !== 0), otherwise match by fieldId for unsaved fields
            const matches = field.id === 0 ? field.fieldId === fieldIdentifier : field.id === fieldIdentifier;
            if (matches) {
              const updatedField = {
                ...field,
                label,
                effectiveLabel: label, // Update effective property for immediate UI feedback
              };
              // Update selected field if it's the one being edited
              const selectedMatches = selectedFieldForEditing
                ? selectedFieldForEditing.id === 0
                  ? selectedFieldForEditing.fieldId === fieldIdentifier
                  : selectedFieldForEditing.id === fieldIdentifier
                : false;
              if (selectedMatches) {
                setSelectedFieldForEditingState(updatedField);
              }
              return updatedField;
            }
            return field;
          }),
        })),
      };

      setTemplateEditable(updatedTemplate);
      if (label.trim() !== '') {
        trackActivity();
        triggerAutoSave(updatedTemplate, 'Updated field label');
      }
    },
    [templateEditable, selectedFieldForEditing, trackActivity, triggerAutoSave]
  );

  const updateFieldDefaultValues = useCallback(
    (fieldIdentifier: number, defaultValues: string[]) => {
      // Track user activity for this input change
      trackActivity();

      const updatedTemplate = {
        ...templateEditable,
        templateSections: templateEditable.templateSections.map((section) => ({
          ...section,
          sectionFields: section.sectionFields.map((field) => {
            // Match by id if saved (id !== 0), otherwise match by fieldId for unsaved fields
            const matches = field.id === 0 ? field.fieldId === fieldIdentifier : field.id === fieldIdentifier;
            if (matches) {
              const updatedField = {
                ...field,
                defaultValues,
                effectiveDefaultValues: defaultValues, // Update effective property for immediate UI feedback
              };
              // Update selected field if it's the one being edited
              const selectedMatches = selectedFieldForEditing
                ? selectedFieldForEditing.id === 0
                  ? selectedFieldForEditing.fieldId === fieldIdentifier
                  : selectedFieldForEditing.id === fieldIdentifier
                : false;
              if (selectedMatches) {
                setSelectedFieldForEditingState(updatedField);
              }
              return updatedField;
            }
            return field;
          }),
        })),
      };

      // Update UI immediately (optimistic)
      setTemplateEditable(updatedTemplate);

      // Trigger auto-save in background
      triggerAutoSave(updatedTemplate, 'Updated field default value');
    },
    [templateEditable, selectedFieldForEditing, trackActivity, triggerAutoSave]
  );

  const updateFieldRequired = useCallback(
    (fieldIdentifier: number, required: boolean) => {
      // Track user activity for this change
      trackActivity();

      const updatedTemplate = {
        ...templateEditable,
        templateSections: templateEditable.templateSections.map((section) => ({
          ...section,
          sectionFields: section.sectionFields.map((field) => {
            // Match by id if saved (id !== 0), otherwise match by fieldId for unsaved fields
            const matches = field.id === 0 ? field.fieldId === fieldIdentifier : field.id === fieldIdentifier;
            if (matches) {
              const updatedField = {
                ...field,
                isRequired: required,
                effectiveIsRequired: required, // Update effective property for immediate UI feedback
              };
              // Update selected field if it's the one being edited
              const selectedMatches = selectedFieldForEditing
                ? selectedFieldForEditing.id === 0
                  ? selectedFieldForEditing.fieldId === fieldIdentifier
                  : selectedFieldForEditing.id === fieldIdentifier
                : false;
              if (selectedMatches) {
                setSelectedFieldForEditingState(updatedField);
              }
              return updatedField;
            }
            return field;
          }),
        })),
      };

      // Update UI immediately (optimistic)
      setTemplateEditable(updatedTemplate);

      // Trigger auto-save in background
      triggerAutoSave(updatedTemplate, 'Updated field required status');
    },
    [templateEditable, selectedFieldForEditing, trackActivity, triggerAutoSave]
  );

  const updateFieldLocked = useCallback(
    (fieldIdentifier: number, locked: boolean) => {
      // Track user activity for this change
      trackActivity();

      const updatedTemplate = {
        ...templateEditable,
        templateSections: templateEditable.templateSections.map((section) => ({
          ...section,
          sectionFields: section.sectionFields.map((field) => {
            // Match by id if saved (id !== 0), otherwise match by fieldId for unsaved fields
            const matches = field.id === 0 ? field.fieldId === fieldIdentifier : field.id === fieldIdentifier;
            if (matches) {
              const updatedField = {
                ...field,
                isLocked: locked,
                effectiveIsLocked: locked, // Update effective property for immediate UI feedback
              };

              // Update selected field if it's the one being edited
              const selectedMatches = selectedFieldForEditing
                ? selectedFieldForEditing.id === 0
                  ? selectedFieldForEditing.fieldId === fieldIdentifier
                  : selectedFieldForEditing.id === fieldIdentifier
                : false;
              if (selectedMatches) {
                setSelectedFieldForEditingState(updatedField);
              }
              return updatedField;
            }
            return field;
          }),
        })),
      };

      // Update UI immediately (optimistic)
      setTemplateEditable(updatedTemplate);

      // Trigger auto-save in background
      triggerAutoSave(updatedTemplate, 'Updated field locked status');
    },
    [templateEditable, selectedFieldForEditing, trackActivity, triggerAutoSave]
  );

  const updateFieldOptions = useCallback(
    (fieldIdentifier: number, options: SectionFieldOption[]) => {
      // Track user activity for this change
      trackActivity();

      // Find the field to get its type for validation
      let fieldType = 'Text';
      for (const section of templateEditable.templateSections) {
        const field = section.sectionFields.find((f) =>
          f.id === 0 ? f.fieldId === fieldIdentifier : f.id === fieldIdentifier
        );
        if (field) {
          fieldType = field.fieldType || 'Text';
          break;
        }
      }

      // Validate field options for SingleSelect/MultiSelect fields
      // Convert SectionFieldOption to FieldOption for validation (use effectiveLabel)
      let validationError = '';
      if (fieldType === 'SingleSelect' || fieldType === 'MultiSelect') {
        const optionsForValidation: FieldOption[] = options.map((opt) => ({
          ...opt,
          label: opt.effectiveLabel, // Use effective label for validation
          isVisible: opt.isDeleted === null ? true : !opt.isDeleted,
        }));
        const validationResult = validateFieldOptions(optionsForValidation);
        if (!validationResult.isValid) {
          // Get the first error message
          const { errors } = validationResult;
          if (errors.emptyLabels) validationError = errors.emptyLabels;
          else if (errors.duplicateLabels) validationError = errors.duplicateLabels;
          else if (errors.minOptions) validationError = errors.minOptions;
        }
      }

      // Update validation errors state
      setFieldOptionValidationErrors((prev) => {
        if (validationError) {
          return { ...prev, [fieldIdentifier]: validationError };
        } else {
          const { [fieldIdentifier]: removed, ...rest } = prev;
          return rest;
        }
      });

      const updatedTemplate = {
        ...templateEditable,
        templateSections: templateEditable.templateSections.map((section) => ({
          ...section,
          sectionFields: section.sectionFields.map((field) => {
            // Match by id if saved (id !== 0), otherwise match by fieldId for unsaved fields
            const matches = field.id === 0 ? field.fieldId === fieldIdentifier : field.id === fieldIdentifier;
            if (matches) {
              const updatedField = {
                ...field,
                sectionFieldOptions: options,
              };
              // Update selected field if it's the one being edited
              const selectedMatches = selectedFieldForEditing
                ? selectedFieldForEditing.id === 0
                  ? selectedFieldForEditing.fieldId === fieldIdentifier
                  : selectedFieldForEditing.id === fieldIdentifier
                : false;
              if (selectedMatches) {
                setSelectedFieldForEditingState(updatedField);
              }
              return updatedField;
            }
            return field;
          }),
        })),
      };

      // Update UI immediately (optimistic)
      setTemplateEditable(updatedTemplate);

      // Only trigger auto-save if validation passes
      if (!validationError) {
        triggerAutoSave(updatedTemplate, 'Updated field options');
      }
      // If validation fails, we still update the UI but don't auto-save
      // This allows users to continue editing without losing their work
    },
    [templateEditable, selectedFieldForEditing, trackActivity, triggerAutoSave]
  );

  return {
    addFieldToSection,
    removeFieldFromSection,
    addFieldToSectionAtIndex,
    updateFieldLabel,
    updateFieldDefaultValues,
    updateFieldRequired,
    updateFieldLocked,
    updateFieldOptions,
  };
};
